import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  useHabits,
  useTodayCompletions,
  useToggleCompletion,
  useDeleteCompletion,
  useApplyFreeze,
  useUserStats,
  useJournalByDate,
  useSaveJournal,
  useQuickThoughts,
  useAddThought,
  useDeleteThought,
  useCreateHabit,
  useCreateHabitGroup,
  useReorderHabits,
  useReorderGroups,
  useCompletionsRange,
  useDeleteHabit,
  useDailyPlan,
  useGeneratePlan,
} from "../hooks/queries";
import { CheckBox } from "../components/terminal/CheckBox";
import { ProgressBar } from "../components/terminal/ProgressBar";
import { CalendarStrip } from "../components/terminal/CalendarStrip";
import { TerminalLine } from "../components/terminal/TerminalLine";
import { DragHandle, SortableList } from "../components/drag/SortableList";
import { todayIST } from "../lib/time";
import type { ApiHabitRow } from "../types/habit";
import type { JournalEntry } from "@shukan/shared";
import { HabitForm } from "../components/HabitForm";
import { ContributionHeatmap } from "../components/terminal/ContributionHeatmap";
import { QuestReflectionModal } from "../components/terminal/QuestReflectionModal";

function getRankTitle(level: number): string {
  if (level < 5) return "Novice";
  if (level < 10) return "Apprentice";
  if (level < 20) return "Warrior";
  if (level < 30) return "Elite";
  return "Master";
}

function getXpProgressPercentage(totalXp: number): number {
  const level = Math.floor(Math.sqrt(totalXp / 50));
  const currentLevelXp = level * level * 50;
  const nextLevelXp = (level + 1) * (level + 1) * 50;
  const diff = nextLevelXp - currentLevelXp;
  if (diff <= 0) return 0;
  const progress = Math.max(0, Math.min(100, Math.floor(((totalXp - currentLevelXp) / diff) * 100)));
  return progress;
}

interface HabitListItemProps {
  habit: ApiHabitRow;
  completion: any;
  hHandleProps: any;
  onToggle: (habitId: string) => void;
  onFreeze: (habitId: string) => void;
  onDeleteRequest: (habit: ApiHabitRow) => void;
}

function HabitListItem({
  habit: h,
  completion: c,
  hHandleProps,
  onToggle,
  onFreeze,
  onDeleteRequest,
}: HabitListItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isDone = !!c && (h.type === "boolean" || c.value >= h.target_value);
  const partial = !!c && !isDone;
  const state = h.freeze_used_today
    ? "frozen"
    : isDone
      ? "done"
      : partial
        ? "partial"
        : h.type === "timer"
          ? "timer"
          : "idle";

  return (
    <li
      className="shukan-line"
      style={{ justifyContent: "space-between", position: "relative" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={() => onDeleteRequest(h)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <CheckBox
          state={state}
          onToggle={() => onToggle(h.id)}
          ariaLabel={`${h.name} ${isDone ? "done" : "not done"}`}
          label={partial ? String(c?.value ?? 0) : undefined}
        />
        <span style={{ color: h.is_active ? "var(--fg)" : "var(--fg-dim)" }}>
          {h.icon} {h.name}
        </span>
      </div>
      <span style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.75rem" }}>
        {isHovered && (
          <button
            type="button"
            className="shukan-btn"
            style={{
              padding: "0.1rem 0.3rem",
              fontSize: "0.7rem",
              color: "var(--danger)",
              borderColor: "var(--border)",
              marginRight: "0.25rem",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRequest(h);
            }}
            title="Delete habit"
          >
            × delete
          </button>
        )}
        <DragHandle
          listeners={hHandleProps.listeners}
          attributes={hHandleProps.attributes}
        />
        <span style={{ color: "var(--accent)" }}>{(h.current_streak ?? 0) > 0 ? `🔥 ${h.current_streak}` : ""}</span>
        <span style={{ color: "var(--fg-dim)" }}>+{h.xp_value}xp</span>
        {!isDone && (h.current_streak ?? 0) > 0 ? (
          <button
            type="button"
            className="shukan-btn"
            style={{ fontSize: "0.7rem", padding: "0.1rem 0.4rem" }}
            onClick={() => onFreeze(h.id)}
          >
            ❄ freeze
          </button>
        ) : null}
      </span>
    </li>
  );
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      return dateParam;
    }
    return todayIST();
  });

  useEffect(() => {
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setSelectedDate(dateParam);
    }
  }, [dateParam]);

  const handleSelectDate = (dStr: string) => {
    setSelectedDate(dStr);
    setSearchParams({ date: dStr });
  };
  const today = todayIST();
  const [habitFormMode, setHabitFormMode] = useState<"habit" | "task" | null>(null);
  const [isThoughtsExpanded, setIsThoughtsExpanded] = useState(false);
  const [isJournalExpanded, setIsJournalExpanded] = useState(false);

  const qc = useQueryClient();
  const stats = useUserStats();
  const habits = useHabits();
  const completions = useTodayCompletions(selectedDate);
  const toggle = useToggleCompletion();
  const del = useDeleteCompletion();
  const freeze = useApplyFreeze();
  const journal = useJournalByDate(selectedDate);
  const saveJournal = useSaveJournal();
  const thoughts = useQuickThoughts();
  const addThought = useAddThought();
  const deleteThought = useDeleteThought();
  const createHabit = useCreateHabit();
  const createGroup = useCreateHabitGroup();
  const deleteHabit = useDeleteHabit();

  const dailyPlan = useDailyPlan(selectedDate);
  const generatePlan = useGeneratePlan();

  const [habitToDelete, setHabitToDelete] = useState<ApiHabitRow | null>(null);
  const [reflectionHabit, setReflectionHabit] = useState<ApiHabitRow | null>(null);

  const [thoughtDraft, setThoughtDraft] = useState("");
  const [journalDraft, setJournalDraft] = useState<string | null>(null);
  const [journalTitleDraft, setJournalTitleDraft] = useState<string | null>(null);

  useEffect(() => {
    function handleTriggerCreateHabit() {
      setHabitFormMode("habit");
    }
    window.addEventListener("shukan-trigger-create-habit", handleTriggerCreateHabit);
    return () => window.removeEventListener("shukan-trigger-create-habit", handleTriggerCreateHabit);
  }, []);

  const allHabits: ApiHabitRow[] = habits.data ?? [];
  const rows = allHabits.filter((h) => {
    if (h.is_one_off) {
      return h.target_date === selectedDate;
    }
    return true;
  });

  // Calculate range of week for selectedDate (Mon-Sun)
  const refDate = new Date(`${selectedDate}T00:00:00Z`);
  const dow = ((refDate.getUTCDay() + 6) % 7) + 1; // Mon=1, Sun=7
  const mondayDate = new Date(refDate.getTime() - (dow - 1) * 24 * 3600 * 1000);
  const sundayDate = new Date(mondayDate.getTime() + 6 * 24 * 3600 * 1000);

  const weekStart = mondayDate.toISOString().slice(0, 10);
  const weekEnd = sundayDate.toISOString().slice(0, 10);

  const weekCompletions = useCompletionsRange(weekStart, weekEnd);

  // Calculate dayStats for the week containing selectedDate
  const dayStats: Record<string, { done: number; partial: number; total: number }> = {};
  for (let i = 0; i < 7; i++) {
    const d = new Date(mondayDate.getTime() + i * 24 * 3600 * 1000);
    const iso = d.toISOString().slice(0, 10);
    const dayComps = (weekCompletions.data ?? []).filter((c) => c.date === iso);
    const dayCompMap = new Map(dayComps.map((c) => [c.habit_id, c]));

    let done = 0;
    let partial = 0;

    const dayHabits = allHabits.filter((h) => {
      if (h.is_one_off) {
        return h.target_date === iso;
      }
      return true;
    });

    for (const h of dayHabits) {
      const c = dayCompMap.get(h.id);
      if (c) {
        const isDone = h.type === "boolean" || c.value >= h.target_value;
        if (isDone) {
          done++;
        } else if (c.value > 0) {
          partial++;
        }
      }
    }

    dayStats[iso] = {
      done,
      partial,
      total: dayHabits.length,
    };
  }

  // Helper function to group and sort habits by group_id for a given period
  const getGroupedForPeriod = (period: string) => {
    const periodRows = rows.filter((r) => (r.time_of_day || "morning") === period);
    const map = new Map<string, {
      id: string;
      name: string;
      icon: string;
      color: string;
      sortOrder: number;
      habits: ApiHabitRow[];
    }>();
    for (const r of periodRows) {
      let g = map.get(r.group_id);
      if (!g) {
        g = {
          id: r.group_id,
          name: r.group_name,
          icon: r.group_icon,
          color: r.group_color,
          sortOrder: r.group_sort_order,
          habits: [],
        };
        map.set(r.group_id, g);
      }
      g.habits.push(r);
    }
    const groupedList = Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder);
    for (const g of groupedList) {
      g.habits.sort((a, b) => a.sort_order - b.sort_order);
    }
    return groupedList;
  };

  const morningGroups = getGroupedForPeriod("morning");
  const afternoonGroups = getGroupedForPeriod("afternoon");
  const eveningGroups = getGroupedForPeriod("evening");

  // Keep a global group list for form setup/placeholders
  const groupedMap = new Map<string, {
    id: string;
    name: string;
    icon: string;
    color: string;
    sortOrder: number;
    habits: ApiHabitRow[];
  }>();
  for (const r of rows) {
    let g = groupedMap.get(r.group_id);
    if (!g) {
      g = {
        id: r.group_id,
        name: r.group_name,
        icon: r.group_icon,
        color: r.group_color,
        sortOrder: r.group_sort_order,
        habits: [],
      };
      groupedMap.set(r.group_id, g);
    }
    g.habits.push(r);
  }
  const grouped = Array.from(groupedMap.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  for (const g of grouped) {
    g.habits.sort((a, b) => a.sort_order - b.sort_order);
  }

  const todaysRows = (completions.data ?? []).filter((c) => c.date === selectedDate);
  const completionByHabit = new Map(todaysRows.map((c) => [c.habit_id, c]));

  const doneCount = todaysRows.filter((c) => {
    const habit = rows.find((h) => h.id === c.habit_id);
    if (!habit) return false;
    if (habit.type === "boolean") return true;
    return c.value >= habit.target_value;
  }).length;

  const xpToday = rows.reduce((acc, h) => {
    const c = completionByHabit.get(h.id);
    if (!c) return acc;
    if (h.type === "boolean" || c.value >= h.target_value) return acc + h.xp_value;
    return acc;
  }, 0);

  function handleToggle(habitId: string) {
    const existing = completionByHabit.get(habitId);
    if (existing) {
      del.mutate({ habitId, date: selectedDate });
    } else {
      const habit = allHabits.find((h) => h.id === habitId);
      if (habit) {
        setReflectionHabit(habit);
      } else {
        toggle.mutate({ habitId, date: selectedDate });
      }
    }
  }

  function handleFreeze(habitId: string) {
    freeze.mutate({ habitId, date: selectedDate });
  }

  const handleReorderGroups = useCallback(
    (orderedIds: string[]) => {
      const previous = qc.getQueryData<ApiHabitRow[]>(["habits"]);
      if (previous) {
        const orderMap = new Map(orderedIds.map((id, idx) => [id, idx]));
        const optimistic = previous.map((r) => {
          const newOrder = orderMap.get(r.group_id);
          return newOrder !== undefined ? { ...r, group_sort_order: newOrder } : r;
        });
        qc.setQueryData(["habits"], optimistic);
      }

      fetchJson("/api/habits/groups/reorder", {
        method: "PATCH",
        body: JSON.stringify({ orderedIds }),
      })
        .then(() => {
          qc.invalidateQueries({ queryKey: ["habits"] });
        })
        .catch((err: unknown) => {
          console.error("[shukan] reorder groups failed, reverting", err);
          if (previous) qc.setQueryData(["habits"], previous);
        });
    },
    [qc],
  );

  const handleReorderHabits = useCallback(
    (groupId: string, orderedIds: string[]) => {
      const previous = qc.getQueryData<ApiHabitRow[]>(["habits"]);
      if (previous) {
        const orderMap = new Map(orderedIds.map((id, idx) => [id, idx]));
        const optimistic = previous.map((r) => {
          if (r.group_id !== groupId) return r;
          const newOrder = orderMap.get(r.id);
          return newOrder !== undefined ? { ...r, sort_order: newOrder } : r;
        });
        qc.setQueryData(["habits"], optimistic);
      }

      fetchJson("/api/habits/reorder", {
        method: "PATCH",
        body: JSON.stringify({ orderedIds }),
      })
        .then(() => {
          qc.invalidateQueries({ queryKey: ["habits"] });
        })
        .catch((err: unknown) => {
          console.error("[shukan] reorder habits failed, reverting", err);
          if (previous) qc.setQueryData(["habits"], previous);
        });
    },
    [qc],
  );

  async function saveJournalFn() {
    if (journalDraft == null && journalTitleDraft == null) return;
    const content = journalDraft ?? journal.data?.content ?? "";
    const title = journalTitleDraft ?? journal.data?.title ?? "";
    await saveJournal.mutateAsync({ date: selectedDate, content, title });
    setJournalDraft(null);
    setJournalTitleDraft(null);
  }

  function thoughtSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!thoughtDraft.trim()) return;
    addThought.mutate({ content: thoughtDraft.trim() });
    setThoughtDraft("");
  }

  async function convertThoughtToTask(thought: { id: string; content: string }) {
    let activeGroupId = grouped[0]?.id;
    if (!activeGroupId) {
      const groupRes = await createGroup.mutateAsync({
        name: "Inbox",
        icon: "◦",
        color: "#f59e0b"
      });
      activeGroupId = groupRes.id;
    }
    if (!activeGroupId) return;
    await createHabit.mutateAsync({
      groupId: activeGroupId,
      name: thought.content,
      type: "boolean",
      icon: "·",
      targetValue: 1,
      xpValue: 10,
      isOneOff: true,
      targetDate: selectedDate
    });
    await deleteThought.mutateAsync(thought.id);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* RPG Header Block */}
      <div
        className="shukan-card"
        style={{
          padding: "0.6rem 0.8rem",
          border: "1px solid var(--border)",
          borderLeft: "4px solid var(--accent)",
          backgroundColor: "var(--bg-elevated)",
          display: "flex",
          flexDirection: "column",
          gap: "0.4rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.95rem", fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: "bold" }}>
              {stats.data?.username || "shukan_user"}@shukan:~$
            </span>
            <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>
              [{getRankTitle(stats.data?.level ?? 0)} Lvl {stats.data?.level ?? 0}]
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.75rem", color: "var(--fg-dim)", fontSize: "0.75rem", fontFamily: "var(--font-mono)" }}>
            <span>XP: {stats.data?.totalXp ?? 0}</span>
            <span>Tracked: {stats.data?.daysTracked ?? 0}d</span>
          </div>
        </div>

        {stats.data?.customQuote && (
          <div style={{ fontSize: "0.8rem", fontStyle: "italic", borderLeft: "2px solid var(--border)", paddingLeft: "0.5rem", color: "var(--fg-dim)", marginTop: "0.1rem" }}>
            "{stats.data.customQuote}"
          </div>
        )}

        {/* Daily Plan / AI Motivation */}
        <div
          style={{
            borderTop: "1px dashed var(--border)",
            paddingTop: "0.4rem",
            marginTop: "0.1rem",
            fontSize: "0.85rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.7rem", color: "var(--accent)", fontWeight: "bold" }}>&gt; DAILY INTENTION</span>
            {selectedDate === today && (
              <button
                type="button"
                className="shukan-btn"
                style={{ fontSize: "0.65rem", padding: "0.05rem 0.3rem" }}
                onClick={() => generatePlan.mutate(today)}
                disabled={generatePlan.isPending}
              >
                {generatePlan.isPending ? "generating..." : dailyPlan.data ? "⟳ regenerate" : "⚡ generate plan"}
              </button>
            )}
          </div>
          {dailyPlan.data?.notes ? (
            <div style={{ color: "var(--fg)", lineHeight: "1.3", fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>
              {dailyPlan.data.notes}
            </div>
          ) : (
            <div style={{ color: "var(--fg-dim)", fontSize: "0.8rem", fontStyle: "italic" }}>
              No intention set for today. Click regenerate or generate to let AI plan your day!
            </div>
          )}
        </div>
      </div>

      {/* Date Navigation */}
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <button
          type="button"
          className="shukan-btn"
          style={{ fontSize: "0.8rem", padding: "0.15rem 0.4rem" }}
          onClick={() => {
            const d = new Date(`${selectedDate}T00:00:00Z`);
            const prev = new Date(d.getTime() - 7 * 24 * 3600 * 1000);
            handleSelectDate(prev.toISOString().slice(0, 10));
          }}
        >
          &lt; prev week
        </button>
        <button
          type="button"
          className="shukan-btn"
          style={{ fontSize: "0.8rem", padding: "0.15rem 0.4rem" }}
          onClick={() => handleSelectDate(today)}
          disabled={selectedDate === today}
        >
          today
        </button>
        <button
          type="button"
          className="shukan-btn"
          style={{ fontSize: "0.8rem", padding: "0.15rem 0.4rem" }}
          onClick={() => {
            const d = new Date(`${selectedDate}T00:00:00Z`);
            const next = new Date(d.getTime() + 7 * 24 * 3600 * 1000);
            handleSelectDate(next.toISOString().slice(0, 10));
          }}
        >
          next week &gt;
        </button>
      </div>

      <CalendarStrip
        today={today}
        dayStats={dayStats}
        onSelect={handleSelectDate}
        selected={selectedDate}
      />

      {selectedDate !== today ? (
        <TerminalLine dim>
          $ viewing past/future date: {selectedDate} ({selectedDate < today ? "history" : "future"})
        </TerminalLine>
      ) : null}

      <TerminalLine prompt accent style={{ flexWrap: "wrap", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", flex: 1, minWidth: "220px", gap: "0.75rem" }}>
          <strong>{selectedDate}</strong>
          <span style={{ color: "var(--fg-dim)", whiteSpace: "nowrap" }}>
            {doneCount}/{rows.length} · {xpToday}xp today
          </span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            className="shukan-btn"
            style={{ fontSize: "0.8rem", padding: "0.1rem 0.5rem" }}
            onClick={() => setHabitFormMode("habit")}
          >
            + add habit
          </button>
          <button
            type="button"
            className="shukan-btn shukan-btn-primary"
            style={{ fontSize: "0.8rem", padding: "0.1rem 0.5rem" }}
            onClick={() => setHabitFormMode("task")}
          >
            + add task
          </button>
        </div>
      </TerminalLine>

      <ProgressBar done={doneCount} total={rows.length} />

      {rows.length === 0 ? (
        <TerminalLine dim>
          $ no habits yet.{" "}
          <button
            type="button"
            className="shukan-btn"
            style={{ color: "var(--accent)", textDecoration: "underline", padding: 0 }}
            onClick={() => setHabitFormMode("habit")}
          >
            create one
          </button>
        </TerminalLine>
      ) : null}

      {/* ---- Period Divisions ---- */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {/* Ohayo (Morning) Period */}
        <section style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ fontSize: "0.9rem", fontFamily: "var(--font-mono)", color: "var(--accent)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.25rem", fontWeight: "bold" }}>
            ☀ Ohayo (Morning)
          </div>
          {morningGroups.length === 0 ? (
            <div style={{ fontSize: "0.8rem", color: "var(--fg-dim)", fontStyle: "italic", paddingLeft: "0.5rem" }}>$ no morning habits scheduled</div>
          ) : (
            <SortableList
              items={morningGroups}
              onReorder={handleReorderGroups}
              renderSection={(grp, handleProps) => (
                <section key={grp.id} style={{ borderLeft: `2px solid ${grp.color}`, paddingLeft: "0.75rem", marginBottom: "0.5rem" }}>
                  <header style={{ color: grp.color, fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <DragHandle listeners={handleProps.listeners} attributes={handleProps.attributes} />
                    <span>[{grp.icon}] {grp.name}</span>
                  </header>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.5rem", listStyle: "none", padding: 0 }}>
                    <SortableList
                      items={grp.habits}
                      onReorder={(orderedIds) => handleReorderHabits(grp.id, orderedIds)}
                      renderItem={(h, hHandleProps) => (
                        <HabitListItem
                          key={h.id}
                          habit={h}
                          completion={completionByHabit.get(h.id)}
                          hHandleProps={hHandleProps}
                          onToggle={handleToggle}
                          onFreeze={handleFreeze}
                          onDeleteRequest={setHabitToDelete}
                        />
                      )}
                    />
                  </ul>
                </section>
              )}
            />
          )}
        </section>

        {/* Afternoon Period */}
        <section style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ fontSize: "0.9rem", fontFamily: "var(--font-mono)", color: "var(--accent)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.25rem", fontWeight: "bold" }}>
            ☼ Afternoon
          </div>
          {afternoonGroups.length === 0 ? (
            <div style={{ fontSize: "0.8rem", color: "var(--fg-dim)", fontStyle: "italic", paddingLeft: "0.5rem" }}>$ no afternoon habits scheduled</div>
          ) : (
            <SortableList
              items={afternoonGroups}
              onReorder={handleReorderGroups}
              renderSection={(grp, handleProps) => (
                <section key={grp.id} style={{ borderLeft: `2px solid ${grp.color}`, paddingLeft: "0.75rem", marginBottom: "0.5rem" }}>
                  <header style={{ color: grp.color, fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <DragHandle listeners={handleProps.listeners} attributes={handleProps.attributes} />
                    <span>[{grp.icon}] {grp.name}</span>
                  </header>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.5rem", listStyle: "none", padding: 0 }}>
                    <SortableList
                      items={grp.habits}
                      onReorder={(orderedIds) => handleReorderHabits(grp.id, orderedIds)}
                      renderItem={(h, hHandleProps) => (
                        <HabitListItem
                          key={h.id}
                          habit={h}
                          completion={completionByHabit.get(h.id)}
                          hHandleProps={hHandleProps}
                          onToggle={handleToggle}
                          onFreeze={handleFreeze}
                          onDeleteRequest={setHabitToDelete}
                        />
                      )}
                    />
                  </ul>
                </section>
              )}
            />
          )}
        </section>

        {/* Oyasumi (Night) Period */}
        <section style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ fontSize: "0.9rem", fontFamily: "var(--font-mono)", color: "var(--accent)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.25rem", fontWeight: "bold" }}>
            ☾ Oyasumi (Night)
          </div>
          {eveningGroups.length === 0 ? (
            <div style={{ fontSize: "0.8rem", color: "var(--fg-dim)", fontStyle: "italic", paddingLeft: "0.5rem" }}>$ no night habits scheduled</div>
          ) : (
            <SortableList
              items={eveningGroups}
              onReorder={handleReorderGroups}
              renderSection={(grp, handleProps) => (
                <section key={grp.id} style={{ borderLeft: `2px solid ${grp.color}`, paddingLeft: "0.75rem", marginBottom: "0.5rem" }}>
                  <header style={{ color: grp.color, fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <DragHandle listeners={handleProps.listeners} attributes={handleProps.attributes} />
                    <span>[{grp.icon}] {grp.name}</span>
                  </header>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.5rem", listStyle: "none", padding: 0 }}>
                    <SortableList
                      items={grp.habits}
                      onReorder={(orderedIds) => handleReorderHabits(grp.id, orderedIds)}
                      renderItem={(h, hHandleProps) => (
                        <HabitListItem
                          key={h.id}
                          habit={h}
                          completion={completionByHabit.get(h.id)}
                          hHandleProps={hHandleProps}
                          onToggle={handleToggle}
                          onFreeze={handleFreeze}
                          onDeleteRequest={setHabitToDelete}
                        />
                      )}
                    />
                  </ul>
                </section>
              )}
            />
          )}
        </section>
      </div>

      {/* ---- Quick Thoughts (Collapsible) ---- */}
      <section style={{ border: "1px solid var(--border)", padding: "0.75rem", borderRadius: "4px" }}>
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsThoughtsExpanded(!isThoughtsExpanded)}
        >
          <span style={{ fontWeight: "bold", color: "var(--accent)", fontSize: "0.85rem", fontFamily: "var(--font-mono)" }}>
            &gt; thoughts ({thoughts.data?.length ?? 0})
          </span>
          <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>
            {isThoughtsExpanded ? "[ collapse - ]" : "[ expand + ]"}
          </span>
        </header>
        {isThoughtsExpanded && (
          <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <form onSubmit={thoughtSubmit} style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
              <input
                className="shukan-input"
                placeholder="+ idea, reminder, ..."
                value={thoughtDraft}
                onChange={(e) => setThoughtDraft(e.target.value)}
              />
              <button type="submit" className="shukan-btn shukan-btn-primary">$ save</button>
            </form>
            {(thoughts.data ?? []).slice(0, 10).map((t) => (
              <div key={t.id} style={{ display: "flex", gap: "0.75rem", fontSize: "0.85rem", color: "var(--fg)", padding: "0.35rem 0", alignItems: "center", borderBottom: "1px dashed var(--border)" }}>
                <span style={{ color: "var(--accent)" }}>·</span>
                <span style={{ flex: 1 }}>{t.content}</span>
                {t.deferTo ? <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)", backgroundColor: "var(--bg-elevated)", padding: "0.1rem 0.3rem", borderRadius: "2px" }}>→ {t.deferTo}</span> : null}
                <button
                  type="button"
                  className="shukan-btn"
                  style={{
                    padding: "0.15rem 0.4rem",
                    fontSize: "0.7rem",
                    color: "var(--accent)",
                    borderColor: "var(--border)",
                  }}
                  onClick={() => convertThoughtToTask(t)}
                  title="Convert to today's task"
                >
                  + task
                </button>
                <button
                  type="button"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--danger)",
                    cursor: "pointer",
                    fontSize: "1.1rem",
                    padding: "0 0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  onClick={() => deleteThought.mutate(t.id)}
                  title="Delete thought"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- Journal (Collapsible) ---- */}
      <section style={{ border: "1px solid var(--border)", padding: "0.75rem", borderRadius: "4px" }}>
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsJournalExpanded(!isJournalExpanded)}
        >
          <span style={{ fontWeight: "bold", color: "var(--accent)", fontSize: "0.85rem", fontFamily: "var(--font-mono)" }}>
            &gt; journal · {selectedDate}
          </span>
          <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>
            {isJournalExpanded ? "[ collapse - ]" : "[ expand + ]"}
          </span>
        </header>
        {isJournalExpanded && (
          <div style={{ marginTop: "0.75rem" }}>
            <JournalEditor
              entry={journal.data ?? null}
              titleDraft={journalTitleDraft}
              setTitleDraft={setJournalTitleDraft}
              draft={journalDraft}
              setDraft={setJournalDraft}
              onSave={saveJournalFn}
            />
          </div>
        )}
      </section>

      <ContributionHeatmap />

      <HabitForm
        key={habitFormMode || "closed"}
        isOpen={habitFormMode !== null}
        onClose={() => setHabitFormMode(null)}
        existingGroups={grouped.map((g) => ({ id: g.id, name: g.name }))}
        defaultDate={selectedDate}
        initialIsOneOff={habitFormMode === "task"}
      />

      {habitToDelete && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
            animation: "shukan-fade-in 150ms ease-out",
          }}
          onClick={() => setHabitToDelete(null)}
        >
          <div
            style={{
              background: "var(--bg)",
              border: "2px solid var(--accent)",
              borderRadius: "4px",
              padding: "1.5rem",
              width: "90%",
              maxWidth: "400px",
              boxShadow: "0 0 20px var(--accent-glow)",
              animation: "shukan-scale-in 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 1rem 0", color: "var(--accent)", fontFamily: "var(--font-mono)" }}>
              &gt; confirm deletion
            </h3>
            <p style={{ margin: "0 0 1.5rem 0", fontSize: "0.9rem", color: "var(--fg)" }}>
              Are you sure you want to delete the habit <strong>"{habitToDelete.name}"</strong>? This action is permanent and will remove all completions and streaks.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="shukan-btn"
                onClick={() => setHabitToDelete(null)}
              >
                cancel
              </button>
              <button
                type="button"
                className="shukan-btn shukan-btn-primary"
                style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={async () => {
                  await deleteHabit.mutateAsync(habitToDelete.id);
                  setHabitToDelete(null);
                }}
              >
                delete
              </button>
            </div>
          </div>
        </div>
      )}

      {reflectionHabit && (
        <QuestReflectionModal
          habit={reflectionHabit}
          onClose={() => setReflectionHabit(null)}
          onSubmit={(customXp, notes) => {
            toggle.mutate({ habitId: reflectionHabit.id, date: selectedDate, customXp, notes });
            setReflectionHabit(null);
          }}
        />
      )}
    </div>
  );
}

interface JournalEditorProps {
  entry: JournalEntry | null;
  titleDraft: string | null;
  setTitleDraft: (v: string | null) => void;
  draft: string | null;
  setDraft: (v: string | null) => void;
  onSave: () => Promise<void>;
}

// Re-import fetchJson here so the inline callbacks can use it directly.
import { fetchJson } from "../lib/api";

function JournalEditor({ entry, titleDraft, setTitleDraft, draft, setDraft, onSave }: JournalEditorProps) {
  const effectiveTitleDraft = titleDraft ?? entry?.title ?? "";
  const effectiveDraft = draft ?? entry?.content ?? "";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <input
        className="shukan-input"
        placeholder="Journal Title (e.g. Work log, Sunday hike)"
        value={effectiveTitleDraft}
        onChange={(e) => setTitleDraft(e.target.value)}
        style={{ fontWeight: "bold", fontSize: "0.95rem" }}
      />
      <textarea
        className="shukan-input"
        style={{ minHeight: "8rem", resize: "vertical", lineHeight: 1.5 }}
        value={effectiveDraft}
        placeholder="what happened today?"
        onChange={(e) => setDraft(e.target.value)}
      />
      <div style={{ marginTop: "0.25rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <button type="button" className="shukan-btn shukan-btn-primary" onClick={onSave}>$ save</button>
        {entry?.aiSummary ? <span style={{ color: "var(--fg-dim)", fontSize: "0.75rem" }}>↳ ai: {entry.aiSummary}</span> : null}
      </div>
    </div>
  );
}
